/**
 * RS-10 — the Shinsa review label a rezept carries, as shown on レセプト発行 (SCR-181).
 *
 * The labels and their order are PLAN §10.2 / §10.4; the backend mirror is
 * app/Support/Sync/RezeptStatusLabel.php. Order matters here too — it is the same lifecycle the clinic
 * reads down the screen.
 *
 * ── 審査済 NOW HAS A SOURCE; 審査中 STILL DOES NOT (2026-08-04) ────────────────────────────────────────
 * The card's 「config sẵn, chờ nguồn」 paid off for one of the two: creating t_claim — Shinsa finishing with
 * a rezept and handing it to 請求 — now travels as `reviewed` and lands here, so this label renders for real
 * and the screen needed no change. It is also where a rezept goes when its 消込 is released.
 *
 * 審査中 stays sourceless on purpose. In Shinsa 「under review」 and 「just arrived」 are the same moment —
 * every rezept enters at RET_KBN = 1 and no column separates them (§10.2(4)(5)) — and 到着済 already occupies
 * it. Inventing a signal for 審査中 would mean claiming something Shinsa never said.
 *
 * ── WHY THERE IS NO ENTRY FOR "BLANK" ────────────────────────────────────────────────────────────────
 * A rezept with no label is not 未送信. 確定 only stamps 未送信 where sync actually runs, so a missing
 * label means RAH has nothing to say — for a clinic outside the sync scope it would otherwise show a
 * badge that can never resolve. The column renders empty; see RezeptListService::shinsaStatusOf().
 *
 * ── WHY 一部不支給 IS ITS OWN LABEL AND NOT A KIND OF 返戻 (Q1) ─────────────────────────────────────────
 * Shinsa's RET_KBN groups five codes that RAH used to treat identically. Four of them (2 記載不備返戻,
 * 3 保険者返戻, 5 資格喪失, 6 返却) mean NOTHING WAS PAID, so 「fix it and submit again」 is right. RET_KBN 9
 * 一部不支給 is the odd one out: part of the claim was already PAID. Sending that rezept back through
 * 返戻処理 asks the clinic to re-claim money it has, so it needs a word of its own on the screen — and it
 * must never wear the red 返戻 badge that means 「register a resubmission month」.
 */

export const SHINSA_STATUS_UNSENT = '未送信';
export const SHINSA_STATUS_SENT = '発送済';
export const SHINSA_STATUS_ARRIVED = '到着済';
export const SHINSA_STATUS_REVIEWING = '審査中';
export const SHINSA_STATUS_REVIEWED = '審査済';
export const SHINSA_STATUS_PAID = '入金済';
export const SHINSA_STATUS_RETURNED = '返戻';
/**
 * Q1 — RET_KBN 9. A TERMINAL verdict like 入金済, not a to-do.
 *
 * 🔴 A BADGE AND ONLY A BADGE (owner's decision, 2026-08-03). The backend writes this string into
 * shinsa_status and changes nothing else — see RezeptStatusHandler::withdrawsReturn. So a rezept that was
 * never returned keeps is_return = 0 and stays off 「返戻処理」, but one that WAS already returned keeps
 * is_return = 1 and STAYS on that screen wearing this badge. Both are expected; the second is the accepted
 * cost of the decision. Do not add FE logic that hides the row on the strength of this label alone —
 * 「返戻処理」 filters on is_return, and the two are deliberately allowed to disagree.
 */
export const SHINSA_STATUS_PARTIAL_UNPAID = '一部不支給';

export type ShinsaStatus =
  | typeof SHINSA_STATUS_UNSENT
  | typeof SHINSA_STATUS_SENT
  | typeof SHINSA_STATUS_ARRIVED
  | typeof SHINSA_STATUS_REVIEWING
  | typeof SHINSA_STATUS_REVIEWED
  | typeof SHINSA_STATUS_PAID
  | typeof SHINSA_STATUS_RETURNED
  | typeof SHINSA_STATUS_PARTIAL_UNPAID;

interface ShinsaStatusDisplay {
  /** antd Tag color. 返戻 is the only one that asks the clinic to act, so it is the only red. */
  color: string;
  /** Tooltip line — what the label means in the clinic's terms, not the system's. */
  hint: string;
}

export const SHINSA_STATUS_DISPLAY: Record<ShinsaStatus, ShinsaStatusDisplay> = {
  [SHINSA_STATUS_UNSENT]: {
    color: 'default',
    hint: 'まだ審査システムへ送信されていません。',
  },
  [SHINSA_STATUS_SENT]: {
    color: 'blue',
    hint: '審査システムが受け取りました。',
  },
  [SHINSA_STATUS_ARRIVED]: {
    // Bucket-grained (clinic × 提出年月 × 施術区分, B20) — every rezept of that group shows the same value.
    color: 'cyan',
    hint: '審査システムで受付が完了しました（提出年月・施術区分単位）。',
  },
  [SHINSA_STATUS_REVIEWING]: {
    color: 'gold',
    hint: '審査中です。',
  },
  [SHINSA_STATUS_REVIEWED]: {
    color: 'geekblue',
    hint: '審査が完了しました。',
  },
  [SHINSA_STATUS_PAID]: {
    color: 'green',
    hint: '入金が確認されました。',
  },
  [SHINSA_STATUS_RETURNED]: {
    color: 'red',
    hint: '返戻されました。内容をご確認のうえ、再提出年月を登録してください。',
  },
  [SHINSA_STATUS_PARTIAL_UNPAID]: {
    // NOT red: on this column red means 返戻 = 「act, register a resubmission month」, and this label means
    // exactly the opposite — do not resubmit. NOT green either: 入金済 is paid in full, this one is not.
    //
    // 'orange' was the first choice and was dropped after measuring the rendered tags in Chromium
    // (2026-08-03, /rezepts/init): orange bg rgb(255,247,230) / text rgb(212,107,8) against 審査中's gold
    // bg rgb(255,251,230) / text rgb(212,136,6) — a 4/255 difference in one channel. Two labels that read
    // as the same colour are worse than an unexpected one, so this takes the one preset that collides with
    // nothing already on the column. The MEANING is carried by the word and the tooltip; the colour's only
    // job here is to be unmistakably neither 返戻 nor 入金済.
    color: 'purple',
    // WRITTEN OUT IN FULL. This used to render as 「一部」 with the full wording one hover away, to keep the
    // 審査状況 column at its 3-character width. Rejected 2026-08-06: 「一部」 on its own is not a verdict —
    // it reads as a partial anything, on the one column that decides whether a clinic chases money or lets
    // it go. The 5-character tag costs ~28px, taken from 受診者名 / 保険者名 on both screens that show it.
    hint: '審査の結果、一部が不支給となりました。返戻ではないため、再提出の必要はありません。支給額をご確認ください。',
  },
};

/** 状態の更新日 — labelled in the tooltip so a date with no caption cannot be misread as a 施術日. */
export const SHINSA_STATUS_AT_LABEL = '更新日';

/** 返戻の種類 — shown in the tooltip here; 返戻処理 has a dedicated column for it (RS-21). */
export const SHINSA_RETURN_TYPE_LABEL = '返戻区分';

/** Column header. 「状況」 next to it is RAH's own 印刷済/確定 — this one is the other system's verdict. */
export const SHINSA_STATUS_COLUMN_TITLE = '審査状況';
